import { InputAdornment, MenuItem, Modal, IconButton } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import BaseButton from "../common/baseButton";
import BaseDateInput from "../common/baseDateInput";
import BaseInput from "../common/baseInput";
import SearchIcon from "@mui/icons-material/Search";
import "./forms.css";
import { FormTypes } from "@/models/enums";
import FormsMessageIndicator from "./formsMessageIndicator/formsMessageIndicator";
import FormsFunDayView from "./formsFunDayView/formsFunDayView";
import FormsGroupOrderView from "./formsGroupOrderView/formsGroupOrderView";
import { useCallback, useEffect, useState } from "react";
import FormsChat from "./formsChat/formsChat";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import { FormResponse, FormsFilterRequest, FormsFilterResponse } from "@/models/forms";
import { checkEditPermissionWithoutAlert, checkPermissionsWithAlert, formatDateTime } from "@/util";
import api from "@/services/api";
import popupService from "@/services/popupService";
import dayjs from "dayjs";
import { PermissionName } from "@/models/apiModels";
import filtersStore from "@/stores/filtersStore";
import TotalComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const Forms = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [forms, setForms] = useState<FormResponse[]>([]);
    const [selectedForm, setSelectedForm] = useState<FormResponse | null>(null);
    const [formsFilter, setFormsFilter] = useState<FormsFilterRequest>({
        keywords: "",
        fromDate: null,
        tillDate: null,
        formType: null,
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });
    const [hasNext, setHasNext] = useState(true);
    const [openAddFormModal, setOpenAddFormModal] = useState(false);
    const [searchCounter, setSearchCounter] = useState(0);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const formTypes = [
        {
            id: FormTypes.FunDay,
            text: t("forms.FunDayForm"),
        },
        {
            id: FormTypes.GroupOrder,
            text: t("forms.GroupOrderForm"),
        },
    ];

    function handleClearFilters() {
        filtersStore.setFormsFilterData(null);
        setFormsFilter((prevData) => ({
            ...prevData,
            keywords: "",
            fromDate: null,
            tillDate: null,
            formType: null,
        }));
        setSearchCounter(searchCounter + 1);
    }

    function handleOnChangeFromDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setFormsFilter((prevData) => ({
            ...prevData,
            fromDate: e ? formattedDate : null,
        }));
    }

    function handleOnChangeTillDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setFormsFilter((prevData) => ({
            ...prevData,
            tillDate: e ? formattedDate : null,
        }));
    }

    const getFormType = (type: FormTypes | null) => {
        if (type?.toString() === FormTypes[FormTypes.FunDay]) {
            return t("forms.FunDayForm");
        } else {
            return t("forms.GroupOrderForm");
        }
    };

    const getFormView = (type: FormTypes | null | undefined) => {
        if (type?.toString() === FormTypes[FormTypes.FunDay]) {
            return <FormsFunDayView formData={selectedForm} />;
        } else if (type?.toString() === FormTypes[FormTypes.GroupOrder]) {
            return <FormsGroupOrderView formData={selectedForm} />;
        } else {
            return "";
        }
    };

    async function onSearchClick() {
        setForms([]);
        popupService.showLoading();
        filtersStore.setFormsFilterData(null);
        setFormsFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    async function getForms(): Promise<FormsFilterResponse> {
        const filterStore = filtersStore.formsFilterData;
        if (filterStore != null) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setFormsFilter(filterStore ?? new FormsFilterRequest());
            const responsePromise = api.getForms(filterStore ?? new FormsFilterRequest());
            return (await responsePromise).data;
        }

        filtersStore.setFormsFilterData(formsFilter);
        const responsePromise = api.getForms(formsFilter);
        return (await responsePromise).data;
    }

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.ListOfForms)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const apiResponse = await getForms();
            setHasNext(apiResponse.hasNext);
            if (formsFilter.pageIndex === 0) {
                setForms(apiResponse.rows);
                setTotal(apiResponse.total);
                setTotalFilter(apiResponse.totalFilter);
            } else {
                setForms((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            if (formsFilter.pageIndex === 0 && (forms[0] == null || forms[0] === undefined)) {
                setSelectedForm(apiResponse.rows[0]);
            }

            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    async function sortBy(sortBy: string) {
        if (formsFilter.orderBy !== sortBy) {
            setFormsFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setFormsFilter((prevData) => ({
                ...prevData,
                asc: !formsFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setForms([]);
        filtersStore.setFormsFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === formsFilter.orderBy) {
            if (formsFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const handleScroll = useCallback(() => {
        const formsContainer = document.getElementById("forms-list-grid");
        const doScroll =
            formsContainer &&
            formsContainer.scrollTop + formsContainer.clientHeight >= formsContainer.scrollHeight &&
            formsContainer.scrollTop > 0;

        if (!doScroll || !hasNext) {
            return;
        }
        filtersStore.setFormsFilterData(null);
        setFormsFilter((prevData) => ({
            ...prevData,
            pageIndex: prevData.pageIndex + 1,
        }));
    }, [formsFilter.pageIndex, hasNext]);

    useEffect(() => {
        const formsContainer = document.getElementById("forms-list-grid");
        if (!formsContainer) {
            return;
        }
        formsContainer.addEventListener("scroll", handleScroll);
        return () => {
            formsContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [formsFilter.pageIndex, formsFilter.orderBy, formsFilter.asc, searchCounter]);

    const addFormsModal = openAddFormModal ? (
        <div>
            <Modal
                open={openAddFormModal}
                onClose={() => setOpenAddFormModal(false)}
                sx={{ backdropFilter: "blur(6px)" }}
            >
                <div className="new-forms-modal-style">
                    <div className="new-forms-modal-header">
                        <CancelIcon
                            onClick={() => setOpenAddFormModal(false)}
                            style={{ color: "#586A84", cursor: "pointer" }}
                        />
                    </div>
                    <div className="new-forms-modal-content">
                        <div onClick={() => navigate("/forms/createFunDay")} className="new-forms-modal-button">
                            <div className="new-forms-modal-button-content">
                                <div id="new-forms-modal-icon" className="new-forms-modal-icon">
                                    <i className="icon-f icon-ViewFormIcon fs-24"></i>
                                </div>
                                <div>{t("forms.FunDayForm")}</div>
                            </div>
                        </div>
                        <div onClick={() => navigate("/forms/createGroupOrder")} className="new-forms-modal-button">
                            <div className="new-forms-modal-button-content">
                                <div id="new-forms-modal-icon" className="new-forms-modal-icon ">
                                    <i className="icon-f icon-FormChatIcon fs-24 new-form-icon-color"></i>
                                </div>
                                <div>{t("forms.GroupOrderForm")}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    ) : (
        ""
    );

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="forms-container">
                <div className="forms-header">
                    {addFormsModal}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                        <div className="forms-header-text">{t("forms.HeaderText")}</div>
                        <div className="forms-header-buttons">
                            <div>
                                {checkEditPermissionWithoutAlert(PermissionName.CreateForms) ? (
                                    <BaseButton
                                        onClick={() => setOpenAddFormModal(true)}
                                        style={{ padding: "0px 26px" }}
                                        className="basic-button"
                                    >
                                        {t("forms.NewFormBtn")}
                                    </BaseButton>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="forms-filter">
                        <div className="font-style forms-span">{t("employee.FilterHeader")}</div>
                        <div className="adaptive_flex" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                            <div className="forms-filter-row">
                                <div>
                                    <BaseInput
                                        className="search-input"
                                        placeholder={`${t("forms.SearchBy")}`}
                                        type="search"
                                        size="small"
                                        value={formsFilter.keywords}
                                        onChange={(event) =>
                                            setFormsFilter((prevData) => ({
                                                ...prevData,
                                                keywords: event.target.value,
                                            }))
                                        }
                                        sx={{
                                            width: "100%",
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>
                                <div>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DesktopDatePicker
                                            className="date-picker-input"
                                            label={t("forms.FromDate")}
                                            inputFormat="DD/MM/YYYY"
                                            value={formsFilter.fromDate}
                                            onChange={handleOnChangeFromDate}
                                            renderInput={(params) => <BaseDateInput {...params} />}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DesktopDatePicker
                                            className="date-picker-input"
                                            label={t("forms.TillDate")}
                                            inputFormat="DD/MM/YYYY"
                                            value={formsFilter.tillDate}
                                            onChange={handleOnChangeTillDate}
                                            renderInput={(params) => <BaseDateInput {...params} />}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div>
                                    <BaseInput
                                        select
                                        label={t("forms.FormType")}
                                        type="search"
                                        size="small"
                                        value={formsFilter.formType ?? ""}
                                        onChange={(event: any) =>
                                            setFormsFilter((prevData) => ({
                                                ...prevData,
                                                formType: event.target.value,
                                            }))
                                        }
                                        sx={{
                                            width: "100%",
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    className="select-clear-icon"
                                                    sx={{
                                                        visibility: formsFilter.formType ? "visible" : "hidden",
                                                    }}
                                                    onClick={() =>
                                                        setFormsFilter((prevData) => ({
                                                            ...prevData,
                                                            formType: null,
                                                        }))
                                                    }
                                                >
                                                    <img src={ClearIcon} alt="Clear icon" />
                                                </IconButton>
                                            ),
                                        }}
                                    >
                                        {formTypes.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.text}
                                            </MenuItem>
                                        ))}
                                    </BaseInput>
                                </div>
                            </div>
                            <div className="filter-search-btn">
                                <div>
                                    <span
                                        onClick={handleClearFilters}
                                        className="link-element clear-all-btn"
                                        style={{
                                            minWidth: "70px",
                                        }}
                                    >
                                        {t("display.ClearAll")}
                                    </span>
                                </div>
                                <BaseButton
                                    style={{ padding: "0px 30px" }}
                                    className="basic-button"
                                    onClick={() => onSearchClick()}
                                >
                                    {t("general.filter")}
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </div>
                <TotalComponent
                    title={t("forms.QuantityOfForms") as string}
                    allCount={total}
                    currentCount={totalFilter}
                />
                <div className="forms-content">
                    <div style={{ position: "relative" }}>
                        <div id="forms-list-grid" className="forms-list-grid">
                            <div className="forms-fake-thead" id="forms-fake-thead"></div>
                            <table>
                                <thead>
                                    <tr>
                                        <td onClick={() => sortBy("Type")}>
                                            <span>
                                                {t("forms.Type")}
                                                <i className={`icon-f ${getArrowClass("Type")} fs-12`} />
                                            </span>
                                        </td>
                                        <td onClick={() => sortBy("Username")}>
                                            <span>
                                                {t("forms.UserName")}
                                                <i className={`icon-f ${getArrowClass("Username")} fs-12`} />
                                            </span>
                                        </td>
                                        <td onClick={() => sortBy("Datetime")}>
                                            <span>
                                                {t("forms.DateTime")}
                                                <i className={`icon-f ${getArrowClass("Datetime")} fs-12`} />
                                            </span>
                                        </td>
                                        <td>&nbsp;</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {forms.map((form, index) => (
                                        <tr
                                            onClick={() => setSelectedForm(form)}
                                            key={index}
                                            className={selectedForm && selectedForm.id === form.id ? "active-form" : ""}
                                        >
                                            <td>{getFormType(form.formType)}</td>
                                            <td>{form.username}</td>
                                            <td>{formatDateTime(form.dateAndTime)}</td>
                                            <td>
                                                <FormsMessageIndicator newChatMessages={form.unreadMessages} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>{getFormView(selectedForm?.formType)}</div>
                    <div>
                        {selectedForm ? <FormsChat userName={selectedForm?.username} formId={selectedForm.id} /> : ""}
                    </div>
                </div>
            </div>
        </CacheProvider>
    );
};

export default Forms;
